import { useState } from 'react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { IUser } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface IUserNotifyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: IUser | null;
}

const UserNotifyDialog = ({
  isOpen,
  onClose,
  user,
}: IUserNotifyDialogProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await api.post(`/backoffice/users/${user.id}/notify`, {
        title,
        message,
      });
      handleReset();
      onClose();
    } catch (e: any) {
      setError(e.message || t('common.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTitle('');
    setMessage('');
    setError(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleReset();
      onClose();
    }
  };

  const isValid = title.trim().length > 0 && message.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="UserManagement_List_notifyModal"
      >
        <DialogHeader>
          <DialogTitle>{t('users.notify_title')}</DialogTitle>
          <DialogDescription>
            {t('users.notify_desc', {
              name: user?.displayName || user?.username || '',
            })}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p
            className="text-sm text-red-600"
            data-testid="UserManagement_List_notifyModalErrorText"
          >
            {error}
          </p>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.notify_subject')}
              <span className="text-red-500">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('users.notify_subject_placeholder')}
              autoFocus
              data-testid="UserManagement_List_notifyModalSubjectInput"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.notify_content')}
              <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('users.notify_content_placeholder')}
              rows={4}
              data-testid="UserManagement_List_notifyModalContentTextArea"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="UserManagement_List_notifyModalCancelButton"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || loading}
            data-testid="UserManagement_List_notifyModalSendButton"
          >
            {loading ? t('users.sending') : t('users.send_notification')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserNotifyDialog;
